import { Component, inject, LOCALE_ID } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, map, of, startWith, switchMap } from 'rxjs';
import { ApiService } from '../api.service';
import { ParticipantSearchResult, ProjectSearchResult, SearchResult } from '../models';

@Component({
  selector: 'app-global-search',
  standalone: true,
  imports: [AsyncPipe, MatIconModule],
  templateUrl: './global-search.html',
  styleUrl: './global-search.scss',
})
export class GlobalSearch {
  private readonly locale = inject(LOCALE_ID);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private api = inject(ApiService);

  // Combined view-model stream with status, query, and results/error
  vm$ = this.route.queryParamMap.pipe(
    map((params) => (params.get('q') ?? '').trim()),
    switchMap((query) => {
      if (!query) {
        return of({ status: 'idle', query } as const);
      }

      return this.api.search(query).pipe(
        map((results: SearchResult) => ({ status: 'success', query, results }) as const),
        startWith({ status: 'loading', query } as const),
        catchError((error) => of({ status: 'error', query, error } as const)),
      );
    }),
  );

  goToParticipant(result: ParticipantSearchResult) {
    this.router.navigate(['projects', result.projectId, 'participants', result.participantId]);
  }

  goToProject(result: ProjectSearchResult) {
    this.router.navigate(['projects', result.projectId]);
  }

  participantName(result: ParticipantSearchResult): string {
    return [result.firstName, result.lastName].filter(Boolean).join(' ').trim();
  }

  // Relative creation label, e.g. "Created 11 years ago" (mirrors project-list).
  formatCreated(createdIso: string): string {
    const dayMs = 86_400_000;
    const rtf = new Intl.RelativeTimeFormat(this.locale, { numeric: 'auto' });
    const t = Date.parse(createdIso);
    const diffMs = Date.now() - t;
    if (diffMs < 0 || diffMs < dayMs) {
      return $localize`Created today`;
    }
    const days = Math.floor(diffMs / dayMs);
    let value: number;
    let unit: Intl.RelativeTimeFormatUnit;
    if (days < 30) {
      value = -days;
      unit = 'day';
    } else {
      const months = Math.floor(days / 30);
      if (months < 24) {
        value = -months;
        unit = 'month';
      } else {
        const years = Math.floor(months / 12);
        value = -years;
        unit = 'year';
      }
    }
    const rel = rtf.format(value, unit);
    return $localize`Created` + ' ' + rel;
  }
}
